'use strict';

const rp_app = globalThis.rp_app || {};
const datadogLogsModule = require('@datadog/browser-logs');

const datadogLogs = datadogLogsModule.datadogLogs || datadogLogsModule;

const DATADOG_CONSENT_GRANTED = 'granted';
const DATADOG_CONSENT_NOT_GRANTED = 'not-granted';
const RP_CONSENT_CATEGORY_PERFORMANCE = 'performance';

initializeDatadogLogs();

rp_app.datadogLogs = datadogLogs;
rp_app.logger = createLogger();

rp_app.track = require('public/js/lib/track');
rp_app.notify = require('public/js/lib/notify').notify;
rp_app.query = require('public/js/lib/query');
rp_app.cacheConfig = require('public/js/lib/cache-config');
// Allow for plugin to override the global object
rp_app.location = globalThis.$location || require('public/js/lib/location')(globalThis);

module.exports = rp_app;

// Helper functions

// Initialize Datadog Logs if configuration is available
function initializeDatadogLogs() {
	if (!globalThis.__DD_CONFIG__ || !globalThis.__DD_CONFIG__.clientToken) {
		return;
	}

	if (!datadogLogs || typeof datadogLogs.init !== 'function') {
		return;
	}

	// Bundled SDKs cannot be gated by script type, so consent is applied through the SDK instead.
	// Stores without a CMP have no consent runtime and keep the previous always-on behavior.
	const consent = rp_app.consent;

	try {
		datadogLogs.init({
			clientToken: globalThis.__DD_CONFIG__.clientToken,
			site: 'datadoghq.com',
			forwardErrorsToLogs: true,			
			sessionSampleRate: 100,
			service: globalThis.__DD_CONFIG__.service,
			env: globalThis.__DD_CONFIG__.env,
			version: globalThis.__DD_CONFIG__.version,
			trackingConsent: consent ? DATADOG_CONSENT_NOT_GRANTED : DATADOG_CONSENT_GRANTED,
			beforeSend: createBeforeSendHook(),
		});
	} catch {
		// Errors during initialization shouldn't break the application so we fail silently
	}

	consent?.onAllowedConsentCategoriesChange(applyDatadogTrackingConsent);
}

/**
 * @param {string[]} allowedConsentCategories
 */
function applyDatadogTrackingConsent(allowedConsentCategories) {
	const isPerformanceAllowed = allowedConsentCategories.includes(RP_CONSENT_CATEGORY_PERFORMANCE);

	try {
		datadogLogs.setTrackingConsent(isPerformanceAllowed ? DATADOG_CONSENT_GRANTED : DATADOG_CONSENT_NOT_GRANTED);
	} catch {
		// Consent updates must never break the page; logging simply stays withheld.
	}
}

const FILTERED_CSP_ELEM_DIRECTIVES = ['script-src-elem', 'style-src-elem'];

/**
 * @typedef {object} DatadogLog
 * @property {string} [message]
 * @property {{ kind?: string, message?: string, stack?: string, file?: string }} [error]
 * @property {{ status_code?: number }} [http]
 * @property {{ title?: string, body?: { effectiveDirective?: string } }} [age]
 */

/**
 * @param {DatadogLog} log
 * @returns {Array<string | undefined>}
 */
function getMessageAndErrorValues(log) {
	return [log.message, log.error?.message];
}

/**
 * @param {DatadogLog} log
 * @returns {Array<string | undefined>}
 */
function getBrowserExtensionValues(log) {
	return [log.message, log.error?.message, log.error?.stack, log.error?.file];
}

/**
 * Builds a filter matching any of `patterns` against the values `getValues` extracts.
 *
 * @param {RegExp[]} patterns
 * @param {(log: DatadogLog) => Array<string | undefined>} [getValues]
 * @returns {(log: DatadogLog) => boolean}
 */
function matchesPatterns(patterns, getValues = getMessageAndErrorValues) {
	return (log) => getValues(log).some(
		(value) => typeof value === 'string' && patterns.some((pattern) => pattern.test(value)),
	);
}

/** @type {Array<(log: DatadogLog) => boolean>} */
const DATADOG_NOISE_LOG_FILTERS = [
	// Datadog: "Client Logs from Chrome Extensions" / "Client Logs from Mozilla Extensions"
	matchesPatterns([/(?:chrome|moz)-extension:/i], getBrowserExtensionValues),
	// Datadog: "Webstore Browser: Uncaught Script Error"
	matchesPatterns([/^Uncaught "Script error\."$|^Script error\.$/]),
	// Datadog: "Webstore Browser: XHR Error" / "Webstore Browser: Fetch Error"
	// Auto-captured by forwardErrorsToLogs (e.g. "XHR error GET …", status_code 0).
	(log) => {
		if (log.http?.status_code === 0) {
			return true;
		}

		const message = log.message;
		if (typeof message !== 'string') {
			return false;
		}

		return /^(XHR|Fetch) error\b/i.test(message);
	},
	// Datadog: "Webstore Browser: GTIN is Invalid"
	matchesPatterns([/Product GTIN .* is invalid!/i]),
	(log) => {
		const kind = log.error?.kind;
		return kind === 'ReferenceError'
			|| kind === 'SyntaxError'
			|| kind === 'TimeoutError'
			|| kind === 'TypeError';
	},
	matchesPatterns([/(?:ReferenceError|SyntaxError|TimeoutError|TypeError):\s*.+/]),
	(log) => {
		const title = log.age?.title;
		const effectiveDirective = log.age?.body?.effectiveDirective;

		return (
			typeof title === 'string'
			&& typeof effectiveDirective === 'string'
			&& /Directive was violated \(\) at/.test(title)
			&& FILTERED_CSP_ELEM_DIRECTIVES.includes(effectiveDirective)
		);
	},
	// Datadog: "Webstore Browser: OptinMonster"
	matchesPatterns([/^\[OptinMonster\]/, /OptinMonster library not loaded\./]),
	// Datadog: "Webstore Browser: Required elements not found. No products hidden."
	matchesPatterns([/Required elements not found\. No products hidden\./]),
];

function createBeforeSendHook() {
	let webstoreMetadata = null;

	const getWebstoreMetadata = () => {
		if (webstoreMetadata !== null) {
			return webstoreMetadata;
		}

		try {
			const body = document.body;
			webstoreMetadata = {
				store_hash: body?.dataset?.storeHash ?? null,
			};
		} catch {
			webstoreMetadata = { store_hash: null };
		}

		return webstoreMetadata;
	};

	return (log) => {
		try {
			if (DATADOG_NOISE_LOG_FILTERS.some((matchesNoise) => matchesNoise(log))) {
				return false;
			}

			// Add webstore metadata to the log to help with debugging
			Object.assign(log, getWebstoreMetadata());
		} catch {
			// If beforeSend fails, allow the log through without metadata
		}

		return true;
	};
}

// Create logger wrapper that uses datadogLogs.logger directly
// Maintains API compatibility with the old Logger class
function createLogger() {
	if (!datadogLogs || !datadogLogs.logger) {
		// Return no-op logger if Datadog isn't initialized
		return {
			info: () => {},
			error: () => {},
		};
	}

	return {
		info: (data = {}) => {
			try {
				if (!data || !data.message) {
					return;
				}
				datadogLogs.logger.info(data.message, data);
			} catch {
				// Silently fail - don't break the app if logging fails
			}
		},
		error: (data = {}) => {
			try {
				if (!data || !data.message) {
					return;
				}
				datadogLogs.logger.error(data.message, data);
			} catch {
				// Silently fail - don't break the app if logging fails
			}
		},
	};
}
